// services/TestService.js
import { projectCachedAxios, noCacheAxios } from '../../../shared/config/cachedAxiosConfig';
import { TEST_ENDPOINTS, FILE_ENDPOINTS } from '../config/projectApiEndpoints';
import { groupingSummaryFolder } from '../config/groupingPaths';
import S3JsonService from './S3JsonService';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import { asyncStorage } from '../../services/storage/asyncStorageService';
import logger from '../../services/logging/logger';

export const TestService = {
  /**
   * Execute a test run
   */
  executeRun: async (
    projectId,
    testSuiteId,
    testCaseId,
    testId,
    decodedFilePath,
    userData,
    options = {}
  ) => {
    try {
      const {
        tenantId = ProjectHeadersService.getTenantId(userData),
        projectName = sessionStorage.getItem('currentProjectName') ||
                     asyncStorage.get('currentProjectName') ||
                     userData.projectName ||
                     'PromptManagerApi',
        s3Bucket = ProjectHeadersService.getBucketName(userData),
        repo = 'https://github.com/cmdquality/platfrom-test-execution.git'
      } = options;

      if (!projectId || !decodedFilePath) {
        throw new Error('Missing required parameters: projectId and decodedFilePath are required');
      }

      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
        tenantId,
        bucketName: s3Bucket
      });
      
      // Construct the runner class path - decodedFilePath should contain the full path
      const runnableFilePath = decodedFilePath.startsWith('projects/') 
        ? decodedFilePath 
        : `projects/${projectId}/${projectName}/${decodedFilePath}`;

      logger.debug('Constructed runnableFilePath', { runnableFilePath });

      // Simplified run payload structure
      const runPayload = {
        projectId,
        tenantId,
        projectName,
        s3Bucket,
        repo,
        runnableFilePath
      };

      logger.debug('Test run request details', {
        url: TEST_ENDPOINTS.createRun(projectId, tenantId),
        payload: runPayload,
        headers
      });

      const response = await noCacheAxios.post(
        TEST_ENDPOINTS.createRun(projectId, tenantId),
        runPayload,
        { headers }
      );

      logger.info('API Response received', { data: response.data });

      return response.data;
    } catch (error) {
      logger.error('Test run error', {
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Run a feature file
   */
  runFeatureFile: async (projectId, filePath, userData) => {
    try {
      if (!projectId || !filePath) {
        throw new Error('Invalid project ID or file path');
      }

      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
      
      const response = await noCacheAxios.post(
        TEST_ENDPOINTS.runFeature(projectId),
        { filePath },
        { headers }
      );
      
      return response.data;
    } catch (error) {
      logger.error('Feature file run error', { error: error.message });
      throw error;
    }
  },

  /**
   * Get a specific run by ID
   */
  getRun: async (projectId, runnableId, runId, userData, tenantId = null, options = {}) => {
    try {
      if (!projectId || !runnableId || !runId) {
        throw new Error('Missing required parameters: projectId, runnableId, or runId');
      }

      // Use provided tenantId or fall back to userData
      const effectiveTenantId = tenantId || ProjectHeadersService.getTenantId(userData);
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
        tenantId: effectiveTenantId
      });

      // Use noCacheAxios when bypassing cache (e.g., polling for run status during execution)
      const axiosInstance = options.bypassCache ? noCacheAxios : projectCachedAxios;
      const response = await axiosInstance.get(
        TEST_ENDPOINTS.getRun(projectId, runnableId, runId, effectiveTenantId),
        { headers }
      );

      return response.data;
    } catch (error) {
      logger.error('Get run error', {
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

/**
   * Get recent runs for a project
   */
  getRecentRunsByProject: async (projectId, userData, limit = 10, tenantId = null, options = {}) => {
    try {
      if (!projectId) {
        throw new Error('Missing required parameter: projectId');
      }

      // Use provided tenantId or fall back to userData
      const effectiveTenantId = tenantId || ProjectHeadersService.getTenantId(userData);
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
        tenantId: effectiveTenantId
      });

      // Add user information to headers for permission validation
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Support optional date range filtering
      const { fromDate, toDate } = options;
      const url = TEST_ENDPOINTS.getRecentRunsByProject(projectId, effectiveTenantId, limit, fromDate, toDate);

      logger.debug('getRecentRunsByProject request', {
        headers,
        url
      });

      // Use noCacheAxios when bypassing cache (e.g., after a test run completes)
      const axiosInstance = options.bypassCache ? noCacheAxios : projectCachedAxios;
      const response = await axiosInstance.get(url, { headers });

      return response.data;
    } catch (error) {
      // Suppress during logout — Network Errors and 401s are expected when token is cleared
      if (error.message === 'Network Error' || error.response?.status === 401) {
        throw error;
      }
      logger.error('Get recent runs error', {
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Get the latest run for a specific runnable
   */
  getLatestRunByRunnable: async (projectId, runnableId, userData, tenantId = null) => {
    try {
      if (!projectId || !runnableId) {
        throw new Error('Missing required parameters: projectId or runnableId');
      }

      // Use provided tenantId or fall back to userData
      const effectiveTenantId = tenantId || ProjectHeadersService.getTenantId(userData);
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
        tenantId: effectiveTenantId
      });
      
      // Construct the URL for the latest run endpoint
      const url = TEST_ENDPOINTS.getLatestRunByRunnable(projectId, runnableId, effectiveTenantId);

      const response = await projectCachedAxios.get(url, { headers });

      return response.data;
    } catch (error) {
      logger.error('Get latest run error', {
        error: error.message,
        response: error.response?.data
      });

      // If it's a 404, it means no runs exist for this runnable
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Get all runs for a specific runnable (keeping for backward compatibility)
   */
  getRunsByRunnable: async (projectId, runnableId, userData, tenantId = null, options = {}) => {
    try {
      if (!projectId || !runnableId) {
        throw new Error('Missing required parameters: projectId or runnableId');
      }

      // Use provided tenantId or fall back to userData
      const effectiveTenantId = tenantId || ProjectHeadersService.getTenantId(userData);
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
        tenantId: effectiveTenantId
      });

      // Use noCacheAxios when bypassing cache (e.g., after a run completes)
      const axiosInstance = options.bypassCache ? noCacheAxios : projectCachedAxios;
      const response = await axiosInstance.get(
        TEST_ENDPOINTS.getRunsByRunnable(projectId, effectiveTenantId, runnableId),
        { headers }
      );

      return response.data;
    } catch (error) {
      logger.error('Get runs by runnable error', {
        error: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  /**
   * Fetch the S3 stream.json snapshot for a run.
   * Returns the same payload shape as the WebSocket stream messages.
   * Used by RunStreamService to backfill state on connect — fast runs may
   * complete before the WebSocket is established, and the snapshot holds the
   * authoritative final state in that case.
   *
   * Throws (404 / network error) when the snapshot doesn't exist yet
   * (new run still starting) or when logPath / projectName are unavailable.
   */
  getRunSnapshot: async (projectId, logPath, projectName, runId, userData, tenantId = null) => {
    if (!logPath || !projectName || !runId) {
      throw new Error('logPath, projectName, and runId are required for snapshot fetch');
    }

    const snapshotKey = `${logPath}/${projectName}_${runId}_stream.json`;
    const effectiveTenantId = tenantId || ProjectHeadersService.getTenantId(userData);
    const baseHeaders = await getAuthHeaders();
    const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData, {
      tenantId: effectiveTenantId,
    });

    const url = FILE_ENDPOINTS.downloadFile(projectId, snapshotKey);
    const response = await noCacheAxios.get(url, { headers });
    return response.data;
  },

  /**
   * The consolidated summary the Core writes when the last batch member
   * finishes. It lives under the project's `groupings/` root, not under
   * Automation Scripts — see `config/groupingPaths` for the full layout.
   *
   * Read through the presigned-URL sequence, the same one the single-run
   * history uses for its summary: the download endpoint hands back a URL, not
   * the file. A 404 means "not written yet", not "broken" — the file only
   * exists once the batch completes.
   *
   * A re-run of the same grouping writes a new run{N}, so earlier runs stay
   * readable at their own paths.
   */
  getConsolidatedSummary: async (
    projectId, projectName, groupingId, batchRunNumber, userData
  ) => {
    if (!projectName || !groupingId || batchRunNumber == null) {
      throw new Error('projectName, groupingId and batchRunNumber are required for the consolidated summary');
    }

    const key = groupingSummaryFolder(projectId, projectName, groupingId, batchRunNumber);
    return S3JsonService.fetchJson(projectId, key, userData);
  },
};